import { supabase } from '../supabaseClient';

export const eventService = {
  // Get all events
  async getAllEvents() {
    try {
      const { data, error } = await supabase
        .from('system_events')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching events:', error);
        return [];
      }
      return data;
    } catch (error) {
      console.error('Unexpected error fetching events:', error);
      return [];
    }
  },

  // Get the currently active event based on date
  async getActiveEvent() {
    try {
      // Fetch all enabled events
      const { data, error } = await supabase
        .from('system_events')
        .select('*')
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching active events:', error);
        return null;
      }
      
      if (!data || data.length === 0) return null;

      // Logic to find the current/upcoming event
      const today = new Date();
      const currentYear = today.getFullYear();
      
      // Calculate next occurrence for each event
      const eventsWithNextDate = data.map(event => {
        if (!event.event_date) return null; // Skip if no date
        
        const [eYear, eMonth, eDay] = event.event_date.split('-').map(Number);
        // Create date object for this year using the event's month (0-indexed) and day
        let nextDate = new Date(currentYear, eMonth - 1, eDay);
        
        // Check if date is strictly in the past (yesterday or before)
        // Set times to 00:00:00 for comparison
        const todayZero = new Date(today);
        todayZero.setHours(0,0,0,0);
        
        // If the calculated date for this year is in the past, move to next year
        if (nextDate < todayZero) {
           nextDate.setFullYear(currentYear + 1);
        }
        
        // Use manual formatting to preserve local date and avoid UTC shifts
        const year = nextDate.getFullYear();
        const month = String(nextDate.getMonth() + 1).padStart(2, '0');
        const day = String(nextDate.getDate()).padStart(2, '0');
        
        return {
          ...event,
          event_date: `${year}-${month}-${day}`,
          diff: nextDate - todayZero
        };
      }).filter(e => e !== null);

      if (eventsWithNextDate.length === 0) {
          // If no events have valid dates, return the first enabled one as fallback
          return data[0]; 
      }

      // Sort by difference (closest upcoming date)
      eventsWithNextDate.sort((a, b) => a.diff - b.diff);
      
      // Return the closest one
      return eventsWithNextDate[0];
      
    } catch (error) {
      console.error('Unexpected error fetching event:', error);
      return null;
    }
  },

  // Create a new event
  async createEvent(eventData) {
    try {
      const { data, error } = await supabase
        .from('system_events')
        .insert([eventData])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { error };
    }
  },

  // Update an event
  async updateEvent(id, eventData) {
    try {
      const { data, error } = await supabase
        .from('system_events')
        .update(eventData)
        .eq('id', id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { error };
    }
  },

  // Delete an event
  async deleteEvent(id) {
    try {
      const { error } = await supabase
        .from('system_events')
        .delete()
        .eq('id', id);

      return { error };
    } catch (error) {
      return { error };
    }
  },

  // Helper to toggle events (for admin usage later)
  async activateEvent(eventId) {
    // First deactivate all
    await supabase.from('system_events').update({ is_active: false }).neq('id', 0);
    
    // Then activate selected
    const { data, error } = await supabase
      .from('system_events')
      .update({ is_active: true })
      .eq('id', eventId)
      .select()
      .single();
      
    return { data, error };
  },

  // Deactivate all events
  async deactivateAllEvents() {
    const { error } = await supabase
      .from('system_events')
      .update({ is_active: false })
      .neq('id', 0);
    
    return { error };
  }
};
